import { Navigate } from 'react-router-dom-v5-compat'

import { useLocalStorage } from '@buffer-mono/popcorn'

import {
  DEFAULT_VIEW_MODE,
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '~publish/components/Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '~publish/components/Calendar/hooks/useCalendarDateRangeViewMode'
import {
  DEFAULT_POST_LIST_VIEW_MODE,
  LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
  type ListOrCalendarViewMode,
} from '~publish/components/PostListOrCalendarViewToggle'
import { useChannelsConnected } from '~publish/contexts/AccountContext'
import {
  allChannels,
  newCalendarSingleChannel,
  newCalendarAllChannels,
  channel,
} from '~publish/legacy/routes'

export const DefaultPath = (): JSX.Element => {
  const connectedChannelsOnOrganization = useChannelsConnected()
  const [listOrCalendarViewMode] = useLocalStorage<ListOrCalendarViewMode>(
    LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_POST_LIST_VIEW_MODE,
  )
  const [calendarDateRangeViewMode] =
    useLocalStorage<CalendarDateRangeViewMode>(
      LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
      DEFAULT_VIEW_MODE,
    )

  const defaultPath = (): string => {
    // NEW
    /**
     * Calculate the default path based on the following rules:
     * - If user has only one connected channel -> Redirect to connected channel
     * - If user has zero or multiple connected channels -> Redirect to all channels
     * - In either view, load calendar/list view based on local storage
     * - Otherwise redirect to all channels queue
     */
    const hasOnlyOneConnectedChannel =
      connectedChannelsOnOrganization?.length === 1
    const shouldRedirectToCalendar = listOrCalendarViewMode === 'calendar'

    if (hasOnlyOneConnectedChannel) {
      const channelId = connectedChannelsOnOrganization?.[0]?.id
      return shouldRedirectToCalendar
        ? newCalendarSingleChannel.buildPathname({
            id: channelId,
            granularity: calendarDateRangeViewMode,
          })
        : channel.getRoute(channelId)
    }

    return shouldRedirectToCalendar
      ? newCalendarAllChannels.buildPathname({
          granularity: calendarDateRangeViewMode,
        })
      : allChannels.route
  }

  return <Navigate to={defaultPath()} />
}
